import * as api from "../../lib/api.js";
import * as store from "../../lib/store.js";
import * as telemetry from "../../lib/telemetry.js";
import { captureAnalytics } from "../analytics";
import type { PushSubscribe } from "../contract/api";

/**
 * Turning on the one notification SSE cannot deliver.
 *
 * The whole reason this module exists is a five-minute gap. `pollResult` in
 * `session.ts` polls `/result` 200 times and then gives up with "it will be here
 * when you come back" -- finalisation regularly outlives that, and the SSE stream
 * is torn down in `finishSession` before it even starts. A backgrounded PWA has
 * no stream to receive on anyway; the browser reclaims the connection.
 *
 * **The permission is spent once and cannot be re-requested.** A declined
 * `Notification.requestPermission()` is final as far as the page is concerned --
 * only the user can undo it, in browser settings, which nobody does. So every
 * path here is built to avoid asking speculatively: `canOfferPush` gates the
 * offer on a deployment that can actually deliver, `enablePush` is only ever
 * called from a real user gesture, and nothing calls it on boot.
 */

/** What the UI needs to decide whether to show the offer, and what to say on it. */
export type PushState =
  /** No service worker, no Push API, or an iOS Safari tab that is not installed. */
  | "unsupported"
  /** The deployment has no VAPID keys. Nothing to offer. */
  | "unconfigured"
  /** Supported and available; the user has not been asked. */
  | "offerable"
  /** Granted and registered with the server. */
  | "on"
  /** Granted in the browser but not currently subscribed here. */
  | "granted"
  /** Declined. Terminal for this origin -- never ask again. */
  | "denied";

/**
 * Whether this browser can do Web Push at all.
 *
 * The `PushManager` test is what excludes an iOS Safari *tab*: on iOS the API
 * exists only for a PWA installed to the Home Screen, so the check doubles as
 * the install requirement without having to sniff for standalone display mode.
 */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * `applicationServerKey` wants raw bytes; the server sends base64url.
 *
 * Written out rather than reached for from a library because it is four lines
 * and the failure it prevents is opaque: `subscribe` rejects with
 * `InvalidCharacterError` or, worse, succeeds against a mis-decoded key and
 * produces a subscription every push then fails to authenticate against.
 */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), "=");
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  // Backed by an explicit ArrayBuffer: `applicationServerKey` takes a
  // `BufferSource`, and a bare `new Uint8Array(n)` types as `ArrayBufferLike`,
  // which admits `SharedArrayBuffer` and so does not satisfy it. This app is
  // cross-origin-isolated for the ONNX threads, so that is a live distinction
  // here rather than a pedantic one.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * The push service's hostname, for telemetry. Never the whole endpoint.
 *
 * The endpoint is a capability -- anything holding it can put a notification on
 * this device, subject only to VAPID -- so it does not go in a telemetry detail
 * any more than it goes in a server log. The host is the part with diagnostic
 * value and none of the risk. Mirrors `service_host` in `notify/dispatch.py`.
 */
function serviceHost(endpoint: string): string {
  try {
    return new URL(endpoint).hostname;
  } catch {
    return "unparseable";
  }
}

/** The deployment's VAPID public key, or null when push is off for it. */
function serverKey(serverConfig: Record<string, unknown> | null): string | null {
  if (!serverConfig?.push_enabled) return null;
  const key = serverConfig.push_public_key;
  return typeof key === "string" && key ? key : null;
}

function matchesServerKey(sub: PushSubscription, key: string): boolean {
  const current = sub.options.applicationServerKey;
  if (!current) return false;
  const actual = new Uint8Array(current);
  const expected = decodeKey(key);
  return actual.length === expected.length && actual.every((value, i) => value === expected[i]);
}

/** Replace subscriptions bound to a retired VAPID key without asking permission again. */
async function subscriptionForKey(reg: ServiceWorkerRegistration, key: string, known?: PushSubscription) {
  const existing = known ?? await reg.pushManager.getSubscription();
  if (existing && matchesServerKey(existing, key)) return { sub: existing, reused: true };
  if (existing) {
    if (!(await existing.unsubscribe())) throw new Error("Could not replace push subscription");
    await api.unsubscribePush({ endpoint: existing.endpoint }).catch(() => {});
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeKey(key),
  });
  return { sub, reused: false };
}

/** Flatten the browser's nested shape into the server's transport-agnostic one. */
function toWireBody(sub: PushSubscription): PushSubscribe {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    transport: "webpush",
    platform: "web",
    p256dh: json.keys?.p256dh ?? null,
    auth: json.keys?.auth ?? null,
  };
}

/**
 * What to show, without asking for anything.
 *
 * Safe to call on every render: it reads `Notification.permission` and the
 * existing subscription, neither of which prompts.
 */
export async function pushState(serverConfig: Record<string, unknown> | null): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  const key = serverKey(serverConfig);
  if (!key) return "unconfigured";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "default") return "offerable";

  const reg = await navigator.serviceWorker.getRegistration();
  const existing = await reg?.pushManager.getSubscription();
  return existing && matchesServerKey(existing, key) ? "on" : "granted";
}

/**
 * Ask, subscribe, register. **Call only from a user gesture.**
 *
 * Safari refuses `requestPermission` outside one outright, and Chrome and
 * Firefox quiet or block a prompt that is not tied to a click -- a quieted
 * prompt reads to the user as nothing happening and to us as a `default`
 * permission that never resolves.
 *
 * Returns the resulting state rather than throwing on a decline: a user saying
 * no is an outcome, not an error, and the caller renders it the same way it
 * renders any other terminal state.
 */
export async function enablePush(
  serverConfig: Record<string, unknown> | null
): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  const key = serverKey(serverConfig);
  if (!key) return "unconfigured";

  const asked = performance.now();
  // Only a prompt that was actually put in front of the person is an answer. An
  // already-`denied` permission short-circuits `requestPermission` back to `denied`
  // without showing anything, so treating it as an answer would count the same
  // terminal decline on every tap of the offer.
  const wasDefault = Notification.permission === "default";
  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  // A real answer to the browser's prompt is the shared event (#1085); a dismissed
  // prompt (still `default`) is not an answer, and the later on/off switch is
  // `notifications_toggled`.
  if (wasDefault && (permission === "granted" || permission === "denied")) {
    captureAnalytics("notification_permission_answered", {
      answer: permission === "granted" ? "granted" : "declined",
    });
  }
  // The single most important client event here, because it is the only
  // irreversible one. A `denied` cannot be re-requested by any means the page
  // has, so the rate of it *is* the measure of whether the offer is being shown
  // at the right moment -- and if that rate ever climbs, the fix is to move the
  // offer, which is a decision that needs this number to be makeable at all.
  telemetry.log("push_permission", { result: permission }, performance.now() - asked);
  if (permission !== "granted") return permission === "denied" ? "denied" : "offerable";

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    telemetry.log("push_subscribe", { result: "no_registration" });
    return "unsupported";
  }

  const started = performance.now();
  try {
    const { sub, reused } = await subscriptionForKey(reg, key);

    await api.subscribePush(toWireBody(sub));
    // Recorded here as well as in syncSubscription: this POST is the same
    // registration, and without the marker the next boot would repeat it.
    await store.savePushSyncRecord({ endpoint: sub.endpoint, syncedAt: Date.now() }).catch(() => {});
    telemetry.log(
      "push_subscribe",
      // `reused` separates the two failure stories that share this event: a
      // browser that would not mint a subscription at all, and one that minted
      // it fine while the server rejected it. `service` is the push service's
      // host, which is what makes "every Firefox device is failing" visible from
      // the client side as well as the server's.
      { result: "ok", reused, service: serviceHost(sub.endpoint) },
      performance.now() - started
    );
    return "on";
  } catch (err) {
    // Reported, then rethrown. The caller renders the failure; this only makes
    // sure a subscribe that dies inside the browser -- which the server never
    // sees, by definition, because nothing ever reached it -- is not invisible.
    telemetry.log(
      "push_subscribe",
      { result: "error", message: String((err as Error).message).slice(0, 64) },
      performance.now() - started
    );
    throw err;
  }
}

/**
 * Reconcile the server's copy with what this browser actually holds.
 *
 * Cheap and idempotent -- the subscribe endpoint upserts on the endpoint -- and
 * worth doing on boot, because the two can drift apart with nothing to notice:
 * a push service rotates an endpoint and `sw.js`'s `pushsubscriptionchange`
 * re-subscribes, but that handler has no device token and cannot register the
 * new endpoint itself.
 *
 * Sent only when the endpoint actually changed since the last successful
 * registration (#434): the endpoint is the whole identity of the row the
 * server upserts, so re-POSTing an unchanged one every boot rewrote a
 * byte-identical record and reassigned it to this device for nothing.
 * The change marker is written only after a successful POST, so a boot
 * whose sync failed tries again next boot.
 *
 * Never prompts. It returns early unless the permission is already granted, so
 * a device that has said no -- or has not been asked -- is untouched by app
 * startup.
 */
export async function syncSubscription(
  serverConfig: Record<string, unknown> | null
): Promise<void> {
  if (!pushSupported()) return;
  const key = serverKey(serverConfig);
  if (!key) return;
  if (Notification.permission !== "granted") return;

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  // Absence may be an explicit in-app opt-out. Only replace an existing
  // subscription here; the enable button handles missing subscriptions.
  const existing = await reg.pushManager.getSubscription();
  if (!existing) {
    telemetry.log("push_sync", { result: "granted_but_unsubscribed" });
    return;
  }
  let sub: PushSubscription;
  let reused: boolean;
  try {
    ({ sub, reused } = await subscriptionForKey(reg, key, existing));
  } catch {
    // Some browsers require a user gesture even with permission granted.
    // pushState reports "granted" so the existing enable button can retry.
    telemetry.log("push_sync", { result: "resubscribe_error" });
    return;
  }

  let record: { endpoint?: unknown; syncedAt?: number } | null = null;
  try {
    record = await store.loadPushSyncRecord();
  } catch {
    // An unreadable marker is treated as "unknown", which means the POST goes
    // out -- the safe direction: at worst one redundant registration.
    record = null;
  }
  // Periodically repair server-side deletion or reassignment even if the browser
  // still holds the same endpoint. A local marker is not permanent server proof.
  if (reused && record?.endpoint === sub.endpoint && record.syncedAt &&
      Date.now() - record.syncedAt < 24 * 60 * 60 * 1000) return;

  try {
    await api.subscribePush(toWireBody(sub));
    // A lost marker only costs one redundant POST next boot; it must not log a
    // registration the server accepted as a failed one.
    await store.savePushSyncRecord({ endpoint: sub.endpoint, syncedAt: Date.now() }).catch(() => {});
    telemetry.log("push_sync", { result: "ok", service: serviceHost(sub.endpoint) });
  } catch (err) {
    // Swallowed on purpose, and this is the one place that is right. A failed
    // reconcile means at worst a missed notification; surfacing it would put an
    // error in front of somebody who did nothing but open the app, about a
    // feature they may not know exists.
    //
    // Swallowed is not the same as unrecorded, though, and that distinction is
    // the whole point of logging it here. This failing repeatedly is how a
    // subscription silently rots: the browser stays subscribed, the server's row
    // goes stale or absent, and neither side has any reason to complain.
    telemetry.log("push_sync", {
      result: "error",
      message: String((err as Error).message).slice(0, 64),
    });
  }
}

/**
 * Drain the notifications the service worker handled while nothing was running.
 *
 * The other half of push telemetry, and the half the server cannot see at all: a
 * push service answering 201 means it accepted the message, which a device that
 * was off, full, or uninstalled produces identically. `sw.js` records arrivals
 * and taps into IndexedDB because it has no device token to report with; this is
 * where they become telemetry.
 *
 * Each entry carries `age_ms` rather than its own timestamp. `telemetry.log`
 * stamps `ts` at flush time, which for these is the wrong moment by anything up
 * to hours -- the age is what makes them placeable against the session they
 * belong to.
 *
 * Cleared after reporting, and cleared even if the flush later fails: a buffer
 * that retries forever would replay the same arrivals on every boot, and a
 * duplicated notification event is worse than a lost one when the thing being
 * counted is *whether delivery works*.
 */
export async function flushPushLog(): Promise<void> {
  let entries: Array<Record<string, unknown>> | null = null;
  try {
    entries = await store.loadPushLog();
  } catch {
    return;
  }
  if (!Array.isArray(entries) || !entries.length) return;

  const now = Date.now();
  for (const entry of entries) {
    const { event, ts, ...detail } = entry as { event?: string; ts?: number };
    if (event !== "push_received" && event !== "push_click") continue;
    telemetry.log(event, {
      ...detail,
      age_ms: typeof ts === "number" ? Math.max(0, now - ts) : null,
    });
  }
  // Flushed before the buffer is cleared, and the order is the whole point.
  // `telemetry.log` only *buffers* -- it posts at 20 events or on a 10 s timer,
  // and `PUSH_LOG_MAX` is 10, so a drain can never reach the count on its own.
  // Clearing first would move these out of IndexedDB and leave them sitting in a
  // RAM buffer for up to ten seconds, during which they exist in neither place
  // if the tab goes away. Boot is exactly when somebody is most likely to
  // navigate straight off again.
  telemetry.flush();
  await store.clearPushLog().catch(() => {});
}

/**
 * Turn notifications off from inside the app.
 *
 * Unsubscribes locally *and* tells the server, in that order, because the local
 * unsubscribe is the one that cannot be undone by a race: once it succeeds the
 * endpoint is dead and the server's next send would prune the row anyway. The
 * explicit delete just means it does not have to wait for a failed send to find
 * out.
 *
 * Does not revoke the permission -- no API can. Re-enabling later therefore
 * costs no second prompt, which is what makes this button safe to offer.
 */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return Notification.permission === "granted" ? "granted" : "offerable";

  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await api.unsubscribePush({ endpoint }).catch(() => {});
  // Logged under the same event as turning it on, distinguished by `result`, so
  // one query returns the whole on/off history for a device. The server sees the
  // DELETE, but only from the device that managed to send one -- a browser that
  // unsubscribed locally and then failed the call is exactly the case the server
  // cannot report on.
  telemetry.log("push_subscribe", { result: "disabled", service: serviceHost(endpoint) });
  return "granted";
}

/**
 * Stop the server sending to this browser on behalf of the device about to be wiped (#336).
 *
 * Server side only, and with the old device token still set: the DELETE is scoped to the
 * calling device. The browser subscription is kept, so the new device re-registers the
 * same endpoint at its next boot without a second permission prompt. Until then the old
 * account's "result ready" pushes would otherwise keep arriving on a device that now
 * belongs to somebody else.
 */
export async function forgetServerSubscription(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api.unsubscribePush({ endpoint: sub.endpoint });
}
